import type { PortalCustomization } from "@redotech/customer-portal-sdk/rpc/schema/portal-customization/get-portal-customization";
import type { CustomFontFamily } from "@redotech/redo-content-builder-common/brand-kit";
import { getFontFamilyWithFallback } from "@redotech/redo-content-builder-common/brand-kit/font-utils";
import {
  buildButtonColorVarDeclarations,
  buildButtonHoverTextRules,
  buildButtonStyleRules,
  buildSecondaryHoverBackgroundRule,
  buildTertiaryColorRules,
} from "./build-button-css-rules";
import { buildCheckboxCssRules } from "./build-checkbox-css-rules";
import { buildCustomFontFaceRules } from "./build-custom-font-css-rules";
import { buildGlobalCardCssRules } from "./build-global-card-css-rules";
import { buildInputFieldRules } from "./build-input-field-css-rules";
import { buildItemCardCssRules } from "./build-item-card-css-rules";
import { buildLoginCardCssRules } from "./build-login-card-css-rules";
import { buildNavbarCssRules } from "./build-navbar-css-rules";
import { buildPageBackgroundCssRules } from "./build-page-background-css-rules";
import { buildPortalBackgroundCssRules } from "./build-portal-background-css-rules";
import { buildPortalLinkCssRules } from "./build-portal-link-css-rules";
import {
  buildTypographySelectorRules,
  buildTypographyVarDeclarations,
} from "./build-typography-css-rules";
import {
  resolveButtonFonts,
  resolveInputFieldFonts,
} from "./resolve-customization-fonts";

export function buildPortalCustomizationCss(
  customization: PortalCustomization,
  {
    scope = "",
    customFontFamilies,
  }: { scope?: string; customFontFamilies?: CustomFontFamily[] | null } = {},
): string {
  // `s`: descendant-selector prefix (empty in global mode). `root`: selector
  // for setting vars ON the scope element itself (`:root` globally).
  const s = scope ? `${scope} ` : "";
  const root = scope || ":root";

  const resolveFontFamily = (font: string | null | undefined) =>
    font ? getFontFamilyWithFallback(font, customFontFamilies) : undefined;

  const rawFonts = customization.typography?.fonts;
  const loginPage = customization.pages?.login;
  const selectedFontFamilies = new Set(
    [
      rawFonts?.heading,
      rawFonts?.body,
      ...(rawFonts?.custom ?? []),
      customization.buttons?.primary?.style?.font,
      customization.buttons?.secondary?.style?.font,
      customization.buttons?.tertiary?.style?.font,
      customization.inputFields?.style?.font,
      loginPage?.typography?.headingFont,
      loginPage?.typography?.bodyFont,
    ].filter((font): font is string => !!font),
  );

  const rawFontWeights = customization.typography?.fontWeights;
  const typography = {
    accentColor: customization.colors?.primary ?? undefined,
    headingFont: resolveFontFamily(rawFonts?.heading),
    bodyFont: resolveFontFamily(rawFonts?.body),
    headingFontWeight: rawFontWeights?.heading ?? undefined,
    bodyFontWeight: rawFontWeights?.body ?? undefined,
    primaryText: customization.typography?.textColors?.primary ?? undefined,
    secondaryText: customization.typography?.textColors?.secondary ?? undefined,
    textCase: customization.typography?.textCase ?? undefined,
  };
  const { primaryButton, secondaryButton, tertiaryButton } = resolveButtonFonts(
    customization.buttons,
    resolveFontFamily,
  );
  const inputFields = resolveInputFieldFonts(
    customization.inputFields,
    resolveFontFamily,
  );

  // 1. CSS variables set on the scope root. Also emit on
  // [data-ui="portal-modal"] so MUI-portaled modals (which render at <body>
  // outside the customer-portal subtree) inherit the same typography and
  // button color tokens. CSS variable inheritance flows through DOM
  // ancestry, so a variable set on `[data-customer-portal]` doesn't reach
  // a sibling-of-`<html>`-rendered modal — but setting it on the modal's
  // own `data-ui` does.
  const cssVarDeclarations: string[] = [
    ...buildTypographyVarDeclarations(typography),
    ...buildButtonColorVarDeclarations("primary", primaryButton?.colors),
    ...buildButtonColorVarDeclarations("secondary", secondaryButton?.colors),
  ];
  const rootVarsRule =
    cssVarDeclarations.length > 0
      ? `${root}, [data-ui="portal-modal"] { ${cssVarDeclarations.join(" ")} }`
      : undefined;

  // 2. Typography selector rules (font-family, color, link color).
  const typographySelectorRules = buildTypographySelectorRules(
    s,
    root,
    typography,
  );

  // 3. Button style rules (radius, border, padding, font) per hierarchy.
  const primaryButtonStyleRules = buildButtonStyleRules(
    s,
    "primary",
    primaryButton,
  );
  const secondaryButtonStyleRules = buildButtonStyleRules(
    s,
    "secondary",
    secondaryButton,
  );
  const tertiaryButtonStyleRules = buildButtonStyleRules(
    s,
    "tertiary",
    tertiaryButton,
  );

  // 4. Tertiary button colors. Tertiary reuses secondary's fg token in the
  // design system, so apply its colors as direct selector rules instead of
  // CSS vars.
  const tertiaryColorRules = buildTertiaryColorRules(s, tertiaryButton?.colors);

  // 4b. Explicit :hover color overrides for primary/secondary, since the
  // design system's hover styles do not always read the fg_hover var.
  const primaryHoverTextRules = buildButtonHoverTextRules(
    s,
    "primary",
    primaryButton?.colors,
  );
  const secondaryHoverTextRules = buildButtonHoverTextRules(
    s,
    "secondary",
    secondaryButton?.colors,
  );
  const secondaryHoverBgRules = buildSecondaryHoverBackgroundRule(
    s,
    secondaryButton?.colors,
  );

  // 5. Input field rules.
  const inputFieldRules = buildInputFieldRules(s, inputFields);

  // 6. Checkbox color rules (selection cards).
  const checkboxRules = buildCheckboxCssRules(s, customization.style?.colors);

  // 7. Item card style rules (reuses selection card border/radius/padding).
  const itemCardRules = buildItemCardCssRules(s, customization.style);

  // 7b. Global card border rules — the login card and the primary display
  // card only (the two "Global cards" surfaces in the builder).
  const globalCardRules = buildGlobalCardCssRules(
    s,
    customization.style?.globalCard,
  );

  // 8. Page background color (paints the area around per-page Cards).
  const pageBackgroundRules = buildPageBackgroundCssRules(
    s,
    customization.branding,
  );

  // 8b. Portal background color — themed at the root so every nested
  // <Flex bgColor="primary"> / <Card> across the portal picks up the
  // merchant color automatically. Carve-outs (e.g. login card, dropdown
  // popovers) win via later cascade. Also flips divider/border tokens and
  // semantic success/error colors on dark themes.
  const portalBackgroundRules = buildPortalBackgroundCssRules(
    root,
    customization.branding,
  );

  // 8c. Portal link color — recolors anchors and link-hierarchy buttons
  // anywhere in the portal, with an auto-derived hover. The link color is
  // also exposed as `--redo-portal-link-color` on the themed root so
  // arbitrary class-styled links can pick it up via fallback. Login is
  // excluded (it uses pages.login.colors.bodyText, which wins on
  // specificity since it targets [data-ui="login-card"] descendants).
  const portalLinkRules = buildPortalLinkCssRules(
    s,
    root,
    customization.branding,
  );

  // 9. Navbar background and text color.
  const navbarRules = buildNavbarCssRules(s, customization.navbar);

  // 10. Login card background + scoped login-only text colors, heading
  // font/weight, card radius, and button color overrides.
  const loginCardRules = buildLoginCardCssRules(
    s,
    loginPage?.branding,
    loginPage?.colors,
    {
      headingFont: resolveFontFamily(loginPage?.typography?.headingFont),
      headingFontWeight: loginPage?.typography?.headingFontWeight,
      bodyFont: resolveFontFamily(loginPage?.typography?.bodyFont),
      bodyFontWeight: loginPage?.typography?.bodyFontWeight,
    },
    loginPage?.buttons,
    loginPage?.inputFields,
  );

  // 11. Portal dropdown carve-out. The profile / single-select dropdowns
  // render via a Popper portal at <body>, so they aren't descendants of any
  // portal-surface selector and inherit the merchant's :root typography
  // overrides. On dark themes that produces near-white text on a white
  // popover, which is unreadable. Pin the dropdown to its design-system
  // default — white surface + neutral primary text — by resetting the
  // typography tokens inside [data-portal-selector="redo-dropdown"]. Always emitted (root
  // scope, not scope-prefixed) so it follows the dropdown into the portal.
  const dropdownRules = [
    '[data-portal-selector="redo-dropdown"] { ' +
      "background-color: var(--redo-colors-base-white) !important; " +
      "color: var(--redo-colors-gray-light-mode-900); " +
      "--redo-colors-background-bg-primary: var(--redo-colors-base-white); " +
      "--redo-colors-text-text-primary: var(--redo-colors-gray-light-mode-900); " +
      "--redo-colors-text-text-secondary: var(--redo-colors-gray-light-mode-700); " +
      "--redo-colors-text-text-tertiary: var(--redo-colors-gray-light-mode-600); " +
      "--return-app-body-text-color: var(--redo-colors-gray-light-mode-900); " +
      "--return-app-title-text-color: var(--redo-colors-gray-light-mode-900);" +
      " }",
    // RedoCommandMenu items render as <RedoButton hierarchy="tertiary"> which
    // picks up the merchant's tertiary text/bg colors (e.g. amber) directly.
    // Inside a dropdown that's an alien accent against the white surface —
    // we want command-menu items to read as neutral list rows. Reset color
    // and background to the design-system defaults; hover gets a subtle grey.
    '[data-portal-selector="redo-dropdown"] [data-hierarchy="tertiary"] { ' +
      "color: var(--redo-colors-gray-light-mode-900) !important; " +
      "background-color: transparent !important; " +
      "outline: none !important;" +
      " }",
    '[data-portal-selector="redo-dropdown"] [data-hierarchy="tertiary"]:hover { ' +
      "background-color: var(--redo-colors-gray-light-mode-100) !important; " +
      "color: var(--redo-colors-gray-light-mode-900) !important;" +
      " }",
    // MUI Drawer / Modal renders a `.MuiBackdrop-root` inside the modal
    // root that dims the page at 50% black. On dark merchant themes that
    // backdrop fully obscures the underlying portal page (dark over dark
    // crushes contrast to nothing visible). Lower the opacity so the page
    // stays readable behind the cart drawer / modal. The outer MuiModal-
    // root carries `data-ui-root="portal-modal"` (set on RedoDrawer /
    // RedoBaseModal — distinct from the inner Paper's `data-ui` so our
    // surface paint rule doesn't fill the viewport).
    '[data-ui-root="portal-modal"] > .MuiBackdrop-root { ' +
      "background-color: rgba(0, 0, 0, 0.25); " +
      " }",
  ];

  // 12. Select-input button fallback: pin the dropdown trigger to secondary-
  // button tokens only when Input fields aren't customized (else buildInputFieldRules owns it).
  const inputColors = customization.inputFields?.colors;
  const hasInputFieldColorCustomization = Boolean(
    inputColors?.background || inputColors?.border || inputColors?.text,
  );
  const selectInputRules = hasInputFieldColorCustomization
    ? []
    : [
        `${s}[data-portal-selector="redo-select-input"] { ` +
          "background-color: var(--redo-component-colors-components-buttons-secondary-button-secondary-bg) !important; " +
          "border-color: var(--redo-component-colors-components-buttons-secondary-button-secondary-border) !important; " +
          "color: var(--redo-component-colors-components-buttons-secondary-button-secondary-fg) !important;" +
          " }",
        `${s}[data-portal-selector="redo-select-input"]:hover { ` +
          "background-color: var(--redo-component-colors-utility-gray-utility-gray-100) !important;" +
          " }",
      ];

  const fontFaceRules = buildCustomFontFaceRules({
    customFontFamilies,
    selectedFontFamilies,
  });

  const rules = [
    ...fontFaceRules,
    rootVarsRule,
    ...typographySelectorRules,
    ...primaryButtonStyleRules,
    ...secondaryButtonStyleRules,
    ...tertiaryButtonStyleRules,
    ...tertiaryColorRules,
    ...primaryHoverTextRules,
    ...secondaryHoverTextRules,
    ...secondaryHoverBgRules,
    ...inputFieldRules,
    ...checkboxRules,
    ...itemCardRules,
    ...pageBackgroundRules,
    ...portalBackgroundRules,
    ...portalLinkRules,
    ...navbarRules,
    ...loginCardRules,
    ...dropdownRules,
    ...selectInputRules,
    ...globalCardRules,
  ].filter((rule): rule is string => rule !== undefined);

  return rules.join("\n");
}
